<template>
  <transition name="v-alerts">
    <div
      v-if="show"
      :class="[
        'v-alerts',
        info && 'v-alerts--info',
        error && 'v-alerts--error',
        global && 'v-alerts--global',
        collapsed && 'v-alerts--collapsed'
      ]"
    >
      <div class="v-alerts__header">
        <svg-icon v-if="!global" alert class="v-alerts__header-icon" />
        <svg-icon v-else close class="v-alerts--global-close" @click="collapsed = true" />
        <v-speak-text
          v-if="global"
          class="v-alerts__speak"
          element-selector=".v-alerts--global .v-alerts__content"
        />
      </div>

      <div
        v-if="!global && dismissible"
        class="v-alerts__dismiss"
        @click="dismiss()">
        <v-icon name="x" width="10" height="10"/>
      </div>

      <div class="v-alerts__alarm" v-on:click="collapsed = !collapsed">
        <div class="v-alerts__alarm-icon">
          <svg-icon alarm />
        </div>
      </div>

      <div class="v-alerts__content">
        <slot />
      </div>
        
      <div
        v-if="hasAlerts"
        :class="[
          'v-alerts__navigation',
          dismissible && 'v-alerts__navigation--bottom'
        ]">
        <v-arrow
          :class="['v-alerts__arrow', showPrevious && 'v-alerts__arrow--previous']"
          direction="left"
          @click="arrowClick(true)"
        />
        <div class="v-alerts__number">{{ currentAlert + 1 }}</div>
        <div class="v-alerts__number">/</div>
        <div class="v-alerts__number">{{ alerts.length }}</div>
        <v-arrow
          :class="['v-alerts__arrow', showNext && 'v-alerts__arrow--next']"
          direction="right"
          @click="arrowClick()"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import Vue from "vue";
import VueCookies from "vue-cookies";
import SvgIcon from "./svg-icon";

Vue.use(VueCookies);

export default {
  components: { "svg-icon": SvgIcon },
  data() {
    return {
      alerts: [],
      currentAlert: 0,
      type: "info",
      collapsed: this.userCollapsed() || !this.expanded,
      show: this.global
    };
  },
  watch: {
    collapsed(newState) {
      if (newState === true) {
        this.$cookies.set("v-alerts-collapsed", "1", 0);
      } else {
        this.$cookies.remove("v-alerts-collapsed");
      }
    }
  },
  computed: {
    hasAlerts() {
      return this.alerts && this.alerts.length > 1;
    },
    showPrevious() {
      return this.currentAlert > 0;
    },
    showNext() {
      return this.currentAlert < ((this.alerts && this.alerts.length - 1) || 0);
    }
  },
  created() {
    this.$emit("controls", { navigate: this.navigate });
  },
  updated() {
    // Call init() here to trigger pagination update
    // in case number of nested alerts has changed.
    this.init();
  },
  mounted() {
    if (this.global || !this.dismissed()) {
      this.init();
      this.show = true;
    }
  },
  methods: {
    init() {
      const alerts = this.getAlerts();
      if (alerts.length !== this.alerts.length) {
        this.alerts = alerts;
        this.setInitialAlert();
      }
    },
    setInitialAlert() {
      const initialAlert = Math.max(
        this.alerts.findIndex(tab => tab.isActive),
        0
      );
      this.setAlert(initialAlert);
    },
    getAlerts() {
      return this.$children.filter(child => child.$options.name === "v-alert");
    },
    arrowClick(backwards) {
      this.navigate(backwards);
      this.$emit("interaction", { backwards });
    },
    navigate(backwards) {
      let newAlert = backwards
        ? Math.max(this.currentAlert - 1, 0)
        : Math.min(this.currentAlert + 1, this.alerts.length);

      let last = newAlert === this.alerts.length;
      if (last && !backwards) {
        newAlert = 0;
      }

      this.setAlert(newAlert);
    },
    setAlert(newIndex) {
      this.alerts.forEach((alert, index) => {
        alert.isActive = newIndex === index;
      });
      this.currentAlert = newIndex;
    },
    userCollapsed() {
      if (this.global) {
        return this.$cookies.isKey("v-alerts-collapsed");
      } else {
        return false;
      }
    },
    dismiss() {
      this.show = false;
      localStorage.setItem(
        "dismissedAlerts",
        JSON.stringify([...this.dismissedAlerts(), this.dismissibleId])
      );
    },
    dismissed() {
      return this.dismissedAlerts().includes(this.dismissibleId);
    },
    dismissedAlerts() {
      return JSON.parse(localStorage.getItem("dismissedAlerts")) || [];
    }
  },
  props: {
    error: {
      type: Boolean,
      default: false
    },
    info: {
      type: Boolean,
      default() {
        return this.$props.error === false;
      }
    },
    global: {
      type: Boolean,
      default: false
    },
    expanded: {
      type: Boolean,
      default() {
        return !this.global;
      }
    },
    dismissible: {
      type: Boolean,
      default: false
    },
    dismissibleId: {
      type: String,
      default: null
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";

$info: #d3e6f6;
$error: #f9dbdc;

@keyframes v-alert-icon-dot {
  0%,
  100% {
    opacity: 0.2;
  }
  60% {
    opacity: 1;
  }
}

@keyframes v-alert-icon-inner {
  0%,
  10%,
  100% {
    opacity: 0.2;
  }
  60% {
    opacity: 1;
  }
}

@keyframes v-alert-icon-outer {
  0%,
  20%,
  100% {
    opacity: 0.2;
  }
  60% {
    opacity: 1;
  }
}

.v-alerts {
  position: relative;
  padding: $spacer;
  border-radius: $border-radius * 0.5;
  transition: all 300ms ease-out;

  &-leave-to {
    opacity: 0;
  }

  &--info {
    background: $info;

    .v-alerts__header-icon {
      color: theme-color("secondary");
    }
  }

  &--error {
    background: $error;

    .v-alerts__header-icon {
      color: theme-color("primary");
    }
  }

  &--global {
    display: flex;
    border-radius: unset;
    color: theme-color("white");
    background: theme-color("primary");
    padding: 0.75 * $spacer $spacer;

    @include media-breakpoint-up(md) {
      display: block;
      width: 356px;
      padding: $spacer;
    }

    &-close {
      cursor: pointer;
    }

    .v-alerts__header {
      @include media-breakpoint-down(sm) {
        left: unset;
        right: $spacer;
      }
    }
  }

  &--collapsed {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 66px;
    height: 66px;
    padding: 0;

    .v-alerts__header,
    .v-alerts__content,
    .v-alerts__navigation {
      display: none;
    }

    .v-alerts__alarm {
      width: 100%;
      height: 100%;
      margin: 0;

      &-icon {
        width: 30px;
        height: 24px;
      }
    }

    @include media-breakpoint-down(sm) {
      display: none;
    }
  }

  &__header {
    position: absolute;
    top: $spacer;
    left: $spacer;
    display: flex;
    justify-content: center;
  }

  &__dismiss {
    display: flex;
    position: absolute;
    right: 0;
    top: 0;
    padding: 10px;
    cursor: pointer;
    opacity: 0.4;

    &:hover {
      opacity: 0.6;
    }
  }

  &--global-close {
    .v-alerts--global & {
      @include media-breakpoint-down(sm) {
        display: none;
      }
    }
  }

  &__speak {
    margin-left: $spacer;

    >>> svg {
      margin: 0 !important;
    }
  }

  &__alarm {
    display: none;
    text-align: center;
    margin: $spacer * 2 auto $spacer auto;
    cursor: pointer;

    .v-alerts--global & {
      display: flex;
      justify-content: center;
      align-items: center;

      @include media-breakpoint-down(sm) {
        margin: 0;
        padding-top: 2px;
        align-items: flex-start;
        pointer-events: none;
      }
    }

    &-icon {
      display: inline-block;
      width: 70px;
      height: 56px;

      .v-alerts--global & {
        @include media-breakpoint-down(sm) {
          width: 20px;
          height: 18px;
        }
      }

      >>> .dot {
        animation-name: v-alert-icon-dot;
        animation-duration: 2000ms;
        animation-fill-mode: forwards;
        animation-timing-function: ease-out;
        animation-iteration-count: infinite;
      }

      >>> .inner {
        animation-name: v-alert-icon-inner;
        animation-duration: 2000ms;
        animation-fill-mode: forwards;
        animation-timing-function: ease-out;
        animation-iteration-count: infinite;
      }

      >>> .outer {
        animation-name: v-alert-icon-outer;
        animation-duration: 2000ms;
        animation-fill-mode: forwards;
        animation-timing-function: ease-out;
        animation-iteration-count: infinite;
      }
    }
  }

  &__content {
    padding: 0 0px 0 24px;
    margin: 0 $spacer 0 $spacer;

    .v-alerts--global & {
      width: 292px;
      margin: $spacer $spacer $spacer * 2 $spacer;

      >>> a {
        color: theme-color("white");
        text-decoration: underline;
      }

      @include media-breakpoint-down(sm) {
        width: auto;
        margin: 0;
        padding: 0 0 0 1 * $spacer;
        overflow: hidden;

        >>> h2 {
          font-size: 1rem;
          margin: 0 30px 0 0;
        }

        >>> div.v-text {
          font-weight: 700;
          font-size: 0.75rem;
        }
      }
    }
  }

  &__navigation {
    position: absolute;
    top: $spacer;
    right: $spacer;
    display: flex;
    height: 24px;
    align-items: center;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    user-select: none;

    &--bottom {
      top: unset;
      bottom: 6px;
      right: 4px;
    }

    .v-alerts--global & {
      @include media-breakpoint-down(sm) {
        display: none;
      }
    }
  }

  &__arrow {
    visibility: hidden;
    margin: 0 $spacer * 0.25;

    &--previous,
    &--next {
      visibility: visible;
    }
  }

  &__number {
    padding-top: 2px;
    min-width: 8px;
  }
}
</style>
